import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { ApiError } from '../../api/index.js';

/**
 * Generic "move" picker. flatUnits is a pre-flattened list of [{ id, name, depth, disabled? }]
 * so we can show the whole tree as an indented select.
 */
export function MoveModal({ open, mode, item, flatUnits, allowRoot = false, onClose, onConfirm }) {
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setBusy(false);
    setError(null);
    setTarget('');
  }, [open]);

  const options = useMemo(() => {
    return flatUnits.filter((u) => !u.disabled);
  }, [flatUnits]);

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      const value = target === '__root__' ? null : target ? Number(target) : null;
      await onConfirm(value);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message || 'Move failed.');
      setBusy(false);
    }
  }

  const title =
    mode === 'unit'
      ? `Move "${item?.name || ''}" to a different parent`
      : `Move "${item?.displayName || ''}" to a different unit`;
  const description =
    mode === 'unit'
      ? 'Pick a new parent unit. Cannot move into the unit\'s own subtree.'
      : 'Pick a destination unit for this person.';

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={title}
      description={description}
      size="md"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary" data-skip-autofocus>
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} disabled={busy || (!target && !allowRoot)} className="btn-primary">
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Moving…
              </>
            ) : (
              <>
                <i className="fa-solid fa-arrow-right-from-bracket" aria-hidden="true" /> Move
              </>
            )}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}
        <label htmlFor="move-target" className="field-label">Destination</label>
        <select
          id="move-target"
          className="field"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          disabled={busy}
        >
          <option value="">— select destination —</option>
          {allowRoot && (
            <option value="__root__">— Root level (no parent) —</option>
          )}
          {options.map((u) => (
            <option key={u.id} value={u.id}>
              {'  '.repeat(u.depth)}
              {u.name}
            </option>
          ))}
        </select>
      </div>
    </Modal>
  );
}
